import { useCallback, useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

// ── Tipos ─────────────────────────────────────────────────────
type Cuota = {
  id: number; periodo: string; nro_cuota: number; descripcion: string;
  monto: number; monto_descuento: number | null; vence_descuento: string | null;
  monto_final: number; vencimiento: string; estado: string;
  fecha_pago: string | null; medio_pago: string | null;
  observaciones: string | null; vencida: boolean;
};

type Objeto = {
  id: number; codigo_objeto: string; descripcion: string;
  tipo_nombre: string; tipo_icono: string;
  cuotas_pendientes: number; deuda_total: number;
};

type Contribuyente = {
  id: number; codigo_contribuyente: string;
  nombre_razon_social: string; dni_cuil: string;
  objetos: Objeto[];
};

type Respuesta = { contribuyentes: Contribuyente[]; deuda_total: number };

function fmt(n: number) {
  return '$ ' + n.toLocaleString('es-AR', { minimumFractionDigits: 2 });
}
function fmtFecha(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function estadoColor(e: string) {
  if (e === 'vencida') return { bg: '#fff5f5', border: '#fca5a5', text: '#dc2626' };
  if (e === 'pagada')  return { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' };
  return { bg: '#fff', border: '#e2e8f0', text: '#374151' };
}
const MEDIO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo', tarjeta_debito: 'Tarjeta Débito',
  tarjeta_credito: 'Tarjeta Crédito', transferencia: 'Transferencia',
  cheque: 'Cheque', mercado_pago: 'Mercado Pago',
  rapipago: 'Rapipago', pagofacil: 'PagoFácil', otro: 'Otro',
};

// ── Panel detalle cuotas de un objeto ─────────────────────────
function DetalleObjeto({
  objeto, onClose,
}: { objeto: Objeto; onClose: () => void }) {
  const [cuotas,    setCuotas]    = useState<Cuota[] | null>(null);
  const [tab,       setTab]       = useState<'pendientes' | 'historial'>('pendientes');
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState<string | null>(null);
  const [selected,  setSelected]  = useState<Set<number>>(new Set());
  const [pagando,   setPagando]   = useState(false);
  const [pagoErr,   setPagoErr]   = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api<Cuota[]>(`/catastro?action=cuotas&objeto_id=${objeto.id}`)
      .then(setCuotas)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [objeto.id]);

  const pendientes = (cuotas ?? []).filter((c) => c.estado !== 'pagada' && c.estado !== 'anulada');
  const historial  = (cuotas ?? []).filter((c) => c.estado === 'pagada' || c.estado === 'anulada');

  const lista = tab === 'pendientes' ? pendientes : historial;

  const hoy = new Date().toISOString().slice(0, 10);
  const totalSel = pendientes
    .filter((c) => selected.has(c.id))
    .reduce((sum, c) => {
      const desc = c.monto_descuento !== null && c.vence_descuento && c.vence_descuento >= hoy;
      return sum + (desc ? +c.monto_descuento! : +c.monto);
    }, 0);

  function toggleSel(id: number) {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    setPagoErr(null);
  }

  async function pagarConMP() {
    if (selected.size === 0) return;
    setPagando(true); setPagoErr(null);
    try {
      const res = await api<{ init_point: string }>('/catastro?action=iniciar_pago', {
        method: 'POST',
        body: JSON.stringify({ cuota_ids: [...selected] }),
      });
      window.location.href = res.init_point;
    } catch (e: any) {
      setPagoErr(e.message || 'No se pudo iniciar el pago');
      setPagando(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[88vh] overflow-y-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex items-start justify-between">
          <div>
            <p className="text-lg font-extrabold text-gray-900 leading-tight">
              {objeto.tipo_icono} {objeto.tipo_nombre}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">{objeto.codigo_objeto}</p>
            {objeto.descripcion && (
              <p className="text-xs text-gray-400 mt-0.5">{objeto.descripcion}</p>
            )}
          </div>
          <button onClick={onClose} className="text-2xl text-gray-400 leading-none ml-4">×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {(['pendientes', 'historial'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="flex-1 py-3 text-sm font-bold capitalize"
              style={{ color: tab === t ? '#7c3aed' : '#9ca3af',
                       borderBottom: tab === t ? '2px solid #7c3aed' : '2px solid transparent' }}
            >
              {t === 'pendientes' ? `Pendientes (${pendientes.length})` : 'Historial'}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-3">
          {loading && <p className="text-center text-gray-400 text-sm py-6">Cargando…</p>}
          {err     && <p className="text-center text-red-500 text-sm py-6">{err}</p>}

          {!loading && lista.length === 0 && (
            <div className="text-center py-8">
              <span className="text-4xl">{tab === 'pendientes' ? '✅' : '📂'}</span>
              <p className="text-gray-600 font-semibold mt-2">
                {tab === 'pendientes' ? 'Sin cuotas pendientes' : 'Sin historial'}
              </p>
            </div>
          )}

          {lista.map((c) => {
            const col = estadoColor(c.estado);
            const descVigente = c.monto_descuento !== null && c.vence_descuento && c.vence_descuento >= hoy;
            const esPendiente = c.estado !== 'pagada' && c.estado !== 'anulada';
            const isSel = selected.has(c.id);
            return (
              <div
                key={c.id}
                className="rounded-2xl border p-4 transition-all"
                style={{
                  backgroundColor: isSel ? '#f5f3ff' : col.bg,
                  borderColor: isSel ? '#7c3aed' : col.border,
                  cursor: esPendiente ? 'pointer' : 'default',
                }}
                onClick={() => esPendiente && toggleSel(c.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  {esPendiente && (
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSel(c.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 flex-shrink-0 accent-violet-600"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm">
                      {c.periodo}
                      {c.nro_cuota > 1 && ` · Cuota ${c.nro_cuota}`}
                    </p>
                    {c.descripcion && (
                      <p className="text-xs text-gray-500 mt-0.5">{c.descripcion}</p>
                    )}
                    {esPendiente && (
                      <p className="text-xs mt-0.5" style={{ color: c.estado === 'vencida' ? '#dc2626' : '#6b7280' }}>
                        Vence: {fmtFecha(c.vencimiento)}
                        {c.estado === 'vencida' && ' · VENCIDA'}
                      </p>
                    )}
                    {descVigente && c.vence_descuento && (
                      <p className="text-xs text-green-600 font-semibold mt-0.5">
                        Descuento hasta {fmtFecha(c.vence_descuento)}
                      </p>
                    )}
                    {c.estado === 'pagada' && c.fecha_pago && (
                      <p className="text-xs text-green-600 mt-0.5">
                        Pagada el {fmtFecha(c.fecha_pago)} · {c.medio_pago ? MEDIO_LABEL[c.medio_pago] ?? c.medio_pago : ''}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {descVigente && c.monto_descuento !== null ? (
                      <>
                        <p className="font-extrabold text-green-600 text-base">{fmt(c.monto_descuento)}</p>
                        <p className="text-xs text-gray-400 line-through">{fmt(c.monto)}</p>
                      </>
                    ) : (
                      <p className="font-bold text-gray-900 text-base">{fmt(c.monto)}</p>
                    )}
                    <p className="text-xs font-semibold mt-0.5" style={{ color: col.text }}>
                      {c.estado === 'pagada' ? '✓ Pagada' : c.estado === 'vencida' ? 'Vencida' : 'Pendiente'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {tab === 'pendientes' && pendientes.length > 0 && (
            <div className="space-y-3 pt-2">
              {selected.size > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-2xl px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-violet-600 font-bold uppercase tracking-wide">Total seleccionado</p>
                    <p className="text-xl font-extrabold text-violet-800">{fmt(totalSel)}</p>
                  </div>
                  <button
                    onClick={pagarConMP}
                    disabled={pagando}
                    className="bg-[#009ee3] text-white font-bold text-sm px-5 py-2.5 rounded-xl active:scale-95 transition-transform disabled:opacity-60"
                  >
                    {pagando ? 'Iniciando…' : '💳 Pagar con Mercado Pago'}
                  </button>
                </div>
              )}
              {pagoErr && (
                <p className="text-xs text-red-600 text-center">{pagoErr}</p>
              )}
              <p className="text-xs text-center text-gray-400">
                También podés pagar en la ventanilla municipal o en un punto de pago habilitado.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function MisImpuestos() {
  const [data,    setData]    = useState<Respuesta | null>(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Objeto | null>(null);
  const pagoParam             = new URLSearchParams(location.search).get('pago');
  const [pagoMsg, setPagoMsg] = useState<{ tipo: 'ok' | 'error' | 'pendiente'; txt: string } | null>(
    pagoParam === 'ok'        ? { tipo: 'ok',        txt: '✅ ¡Pago realizado! Las cuotas fueron acreditadas.' }
    : pagoParam === 'error'   ? { tipo: 'error',     txt: '❌ El pago no se completó. Intentá de nuevo.' }
    : pagoParam === 'pendiente'? { tipo: 'pendiente', txt: '⏳ Pago en proceso. Te notificaremos cuando se acredite.' }
    : null
  );

  const cargar = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setData(await api<Respuesta>('/catastro?action=mis_objetos'));
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const sinContribuyentes = !loading && data && data.contribuyentes.length === 0;
  const deudaTotal        = data?.deuda_total ?? 0;

  return (
    <>
      <TopBar title="Mis impuestos" back />

      <main className="max-w-md mx-auto px-4 py-5 pb-28 space-y-4">

        {pagoMsg && (
          <div
            className="rounded-2xl px-4 py-3 text-sm font-semibold"
            style={{
              background: pagoMsg.tipo === 'ok' ? '#d1fae5' : pagoMsg.tipo === 'error' ? '#fee2e2' : '#fef3c7',
              color:      pagoMsg.tipo === 'ok' ? '#065f46' : pagoMsg.tipo === 'error' ? '#991b1b' : '#92400e',
            }}
          >
            {pagoMsg.txt}
            <button onClick={() => setPagoMsg(null)} className="ml-3 opacity-60 text-lg leading-none">×</button>
          </div>
        )}

        {loading && <p className="text-center text-gray-400 text-sm py-10">Cargando…</p>}
        {err     && <p className="text-center text-red-500 text-sm py-10">{err}</p>}

        {/* Banner deuda total */}
        {!loading && data && data.contribuyentes.length > 0 && (
          <div
            className="rounded-2xl px-5 py-4 text-white"
            style={{ backgroundColor: deudaTotal > 0 ? '#dc2626' : '#16a34a' }}
          >
            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
              {deudaTotal > 0 ? 'Deuda total' : 'Sin deuda pendiente'}
            </p>
            <p className="text-3xl font-extrabold">{fmt(deudaTotal)}</p>
          </div>
        )}

        {/* Contribuyentes y objetos */}
        {!loading && data?.contribuyentes.map((contrib) => (
          <div key={contrib.id}>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1 mb-2">
              {contrib.nombre_razon_social}
              <span className="ml-2 font-normal normal-case text-gray-400">
                · {contrib.dni_cuil}
              </span>
            </p>

            {contrib.objetos.length === 0 && (
              <p className="text-sm text-gray-400 px-1">Sin objetos imponibles</p>
            )}

            {contrib.objetos.map((obj) => (
              <button
                key={obj.id}
                onClick={() => setDetalle(obj)}
                className="w-full bg-white border border-app rounded-2xl px-4 py-4 flex items-center gap-3 text-left mb-3 active:scale-[0.98] transition-transform"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ backgroundColor: '#7c3aed18', border: '1.5px solid #7c3aed44' }}
                >
                  {obj.tipo_icono || '🏛️'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm leading-tight">{obj.tipo_nombre}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{obj.codigo_objeto}</p>
                  {obj.descripcion && (
                    <p className="text-xs text-gray-400 truncate">{obj.descripcion}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  {obj.cuotas_pendientes > 0 ? (
                    <>
                      <p className="font-extrabold text-red-600 text-base">{fmt(obj.deuda_total)}</p>
                      <p className="text-xs text-red-400">
                        {obj.cuotas_pendientes} cuota{obj.cuotas_pendientes !== 1 ? 's' : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs font-bold text-green-600">Al día ✓</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}

        {/* Sin datos */}
        {sinContribuyentes && (
          <div className="text-center py-12">
            <span className="text-5xl">🏛️</span>
            <p className="text-gray-700 font-bold text-lg mt-4">Sin impuestos registrados</p>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">
              Cuando el municipio te asigne impuestos aparecerán acá automáticamente.
            </p>
          </div>
        )}

      </main>

      {detalle && (
        <DetalleObjeto objeto={detalle} onClose={() => setDetalle(null)} />
      )}
    </>
  );
}
